'use client';

import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl';
import TrailPageType from '@alltrails/amplitude/enums/TrailPageType';
import logTrailDetailsActionTapped from '@alltrails/amplitude/events/logTrailDetailsActionTapped';
import logTrailDetailsSeeAllClicked from '@alltrails/amplitude/events/logTrailDetailsSeeAllClicked';
import getScreenReaderRatingText from '@alltrails/core/utils/getScreenReaderRatingText';
import scrollToSectionId from '@alltrails/core/utils/scrollToSectionId';
import Button from '@alltrails/denali/components/Button';
import Link from '@alltrails/denali/components/Link';
import StarFilled from '@alltrails/denali/icons/StarFilled';
import type { TrailId } from '@alltrails/types/TrailId';
import { REVIEWS_SECTION_ID } from '@/utils/constants';
import styles from './ReviewsEntryPoint.module.scss';

type ReviewsEntryPointProps = {
  rating: number;
  reviewCount: number;
  trailId: TrailId;
  trailPageType: TrailPageType;
};

const ReviewsEntryPoint = ({ rating, reviewCount, trailId, trailPageType }: ReviewsEntryPointProps) => {
  const intl = useIntl();

  const onReviewsClick = () => {
    logTrailDetailsActionTapped({
      trail_id: String(trailId),
      trail_page_action: 'show all reviews',
      trail_page_location: 'chat gpt card', // TODO: update analytics - https://alltrails.atlassian.net/browse/DISCO-25162
      trail_page_type: trailPageType
    });
    scrollToSectionId(REVIEWS_SECTION_ID);
  };

  const onShowAllClick = () => {
    logTrailDetailsSeeAllClicked({ trail_id: Number(trailId), location: 'reviews', trail_page_type: trailPageType });
    scrollToSectionId(REVIEWS_SECTION_ID);
  };

  return (
    <div className={styles.container}>
      <div className={styles.ratingGroup}>
        <div className={styles.rating}>
          <span className={styles.ratingValue}>
            <FormattedNumber minimumFractionDigits={1} value={rating} />
          </span>
          <span role="img" aria-label={getScreenReaderRatingText(intl, rating)}>
            <StarFilled color="--color-text-primary" size="xs" />
          </span>
        </div>
        <span className={styles.reviewCount}>
          (
          <Link onClick={onReviewsClick} className={styles.reviewCountLink}>
            <FormattedMessage defaultMessage="{reviewCount, plural, one {# review} other {# reviews}}" values={{ reviewCount }} />
          </Link>
          )
        </span>
      </div>
      <Button
        text={<FormattedMessage defaultMessage="Show all" />}
        size="sm"
        variant="default"
        onClick={onShowAllClick}
        title={intl.formatMessage({ defaultMessage: 'Show all reviews' })}
      />
    </div>
  );
};

export default ReviewsEntryPoint;
